import { html, css, LitElement } from 'lit-element';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { clone, cloneDeep } from 'lodash-es';
import { library, dom } from "@fortawesome/fontawesome-svg-core";
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';

import './badge.js';
import '@material/mwc-select';
import '@material/mwc-list/mwc-list-item';

import { user, functions, db } from './intrivity_app.js';
import {
    emitEvent, INTRIVITY_LOGIN, INTRIVITY_NAV_SELECT, INTRIVITY_GROUP_SELECT,
    buildCombinedReport, buildCombinedGroupReport, AI_SUMMARY_SOURCES,
    aiDomainColor, AI_SUMMARY_PRINT_STYLE, buildAiSummarySectionHtml, cascadeAiEnabled,
    openPrintWindow, resolvePdfMode, generateAndOpenSummaryPdf, PDF_MODE_BROWSER, PDF_MODE_BACKEND,
    buildTeamMembers, isFlagOn, formatAiGeneratedAt
} from './utility.js'
import { a11yStyles, accessibleTextColor } from './a11y.js';
import { t as translate, tHtml, Localized, formatDate, getLocale } from './i18n.js';
import { englishDomain } from './domains.js';

// translate() rather than t(): this element has a `t` (Time score) property.
export class IntrivityNameBannerApp extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            .banner {
                width: 100%;
                border-bottom: 7px solid rgb(250, 166, 53);
                display: grid;
                grid-template-columns: 3fr 1fr ;
            }
            .banner_badge {
                display: grid;
                grid-template-columns: 50px 1fr ;
                margin: auto;
                cursor: pointer;
            }
            .ai-btn {
                margin-left: 12px;
                margin-bottom: 1px;
                padding: 4px 14px;
                background: rgb(250, 166, 53);
                /* White on this orange is 2.0:1; near-black is 8.8:1. */
                color: #1a1a1a;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 0.85em;
                font-weight: 600;
                white-space: nowrap;
                display: inline-flex;
                align-items: center;
            }
            .ai-btn-icon {
                width: 1em;
                height: 1em;
                margin-right: 6px;
                flex-shrink: 0;
            }
            .ai-btn:disabled {
                opacity: 0.6;
                cursor: default;
            }
            .ai-overlay {
                position: fixed;
                top: 0; left: 0;
                width: 100%; height: 100%;
                background: rgba(0,0,0,0.55);
                z-index: 9999;
                overflow-y: auto;
                display: flex;
                align-items: flex-start;
                justify-content: center;
                padding: 40px 16px;
                box-sizing: border-box;
            }
            .ai-modal {
                background: white;
                border-radius: 8px;
                padding: 32px;
                max-width: 960px;
                width: 100%;
                box-shadow: 0 8px 32px rgba(0,0,0,0.3);
                box-sizing: border-box;
                overflow: hidden;
            }
            .ai-modal h2 {
                margin-top: 0;
            }
            .ai-close {
                float: right;
                background: none;
                border: none;
                font-size: 1.4em;
                cursor: pointer;
                line-height: 1;
                padding: 0;
            }
            .ai-print-btn {
                float: right;
                margin-right: 16px;
                padding: 4px 14px;
                background: none;
                /* Darkened for contrast; the border keeps the brand orange. */
                color: rgb(166, 97, 0);
                border: 1px solid rgb(250, 166, 53);
                border-radius: 4px;
                cursor: pointer;
                font-size: 0.85em;
                font-weight: 600;
            }
            .table-scroll {
                width: 100%;
                overflow-x: auto;
            }
            .summary-table {
                width: 100%;
                min-width: 640px;
                border-collapse: collapse;
                margin-top: 16px;
                font-size: 0.9em;
            }
            @media (max-width: 600px) {
                .banner {
                    grid-template-columns: 1fr;
                }
                .ai-modal {
                    padding: 16px;
                }
            }
            .summary-table th, .summary-table td {
                border: 1px solid #ddd;
                padding: 10px 12px;
                text-align: left;
                vertical-align: top;
            }
            .summary-table th {
                /* Headers sit on the four domain colours; white on those runs
                   1.7:1 to 3.7:1. Near-black clears AA on every one. */
                color: #1a1a1a;
                font-weight: 600;
            }
            .summary-table td:first-child {
                font-weight: 600;
                white-space: nowrap;
                background: #f5f5f5;
                width: 100px;
            }
            .score-line {
                font-size: 0.78em;
                color: rgba(255,255,255,0.85);
                font-weight: normal;
                margin-top: 3px;
            }
            .item-title {
                font-weight: 600;
                margin-bottom: 3px;
            }
            .item-desc {
                margin-bottom: 10px;
                line-height: 1.4;
            }
            .team-members {
                display: flex;
                flex-wrap: wrap;
                gap: 8px 16px;
                margin-top: 8px;
            }
            .team-member {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                white-space: nowrap;
                font-size: 1em;
            }
            .team-dynamics {
                /* Bleeds horizontally to the modal's edges, but no longer
                   pulls a negative bottom margin since it's not the last
                   element anymore — that used to make Sources/disclaimer
                   overlap it. */
                margin: 24px -32px 20px -32px;
                padding: 20px 32px;
                background: #e3e3e3;
                border-top: 3px solid rgb(250, 166, 53);
                line-height: 1.5;
            }
            .generated-at {
                font-size: 0.75em;
                color: #aaa;
                margin-top: 14px;
                text-align: right;
            }
            .sources {
                margin-top: 18px;
                padding-top: 10px;
                border-top: 1px solid #eee;
                font-size: 0.72em;
                color: #999;
                line-height: 1.5;
            }
            .sources a {
                color: #999;
            }
            .disclaimer {
                margin-top: 14px;
                padding-top: 8px;
                border-top: 1px solid #eee;
                font-size: 0.72em;
                color: #999;
                line-height: 1.5;
                font-style: italic;
            }
            @media print {
                intrivity-group-selector { display: none; }
                .ai-btn { display: none; }
                * {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            }
        `];
    }

    static get properties() {
        return {
            user_profile:        { type: Object },
            group_report:        { type: Object },
            group_reports:       { type: Array },
            report:              { type: Object },
            message_domain:      { type: Object },
            relationship_domain: { type: Object },
            time_domain:         { type: Object },
            space_domain:        { type: Object },
            m: { type: Number },
            r: { type: Number },
            t: { type: Number },
            s: { type: Number },
            size:      { type: Number },
            printable: { type: Boolean },
            _aiSummary:    { type: Object },
            _aiLoading:    { type: Boolean },
            _aiError:      { type: String },
            _showAiModal:  { type: Boolean },
            _groupDue:     { type: String },
            _parentGroupTitle: { type: String },
            _aiFeatureEnabled: { type: Boolean },
            _pdfMode: { type: String },
            _pdfLoading: { type: Boolean },
        };
    }

    constructor() {
        super();
        this.size = 1;
        this._aiLoading = false;
        this._showAiModal = false;
        this._groupDue = undefined;
        this._parentGroupTitle = undefined;
        this._aiFeatureEnabled = false;
        this._pdfMode = PDF_MODE_BROWSER;
        this._pdfLoading = false;
    }

    async updated(changedProps) {
        // Move focus into the AI summary dialog when it opens, so keyboard and
        // screen-reader users land inside it rather than behind it (WCAG 2.4.3).
        if (changedProps.has('_showAiModal') && this._showAiModal) {
            const modal = this.shadowRoot && this.shadowRoot.querySelector('.ai-modal');
            if (modal) modal.focus();
        }

        // Clear cached summary when the selected group changes
        if (changedProps.has('group_report') && this.group_report) {
            this._aiSummary = undefined;
            this._aiError = undefined;
            this._showAiModal = false;
        }

        // Read live from Firestore rather than the cached report JSON, so
        // an admin setting/changing the due date takes effect immediately.
        if (changedProps.has('group_report') && this.group_report && this.group_report.group) {
            const groupId = this.group_report.group;
            // Fail closed (treat as "not yet due"/AI-disabled) while
            // (re)checking, and guard against a slower fetch for a
            // since-abandoned group resolving after a newer one and
            // clobbering it.
            this._groupDue = undefined;
            this._parentGroupTitle = undefined;
            this._aiFeatureEnabled = false;
            try {
                const snap = await getDoc(doc(db, 'groups', groupId));
                const data = snap.exists() ? snap.data() : null;
                let due = data && data.due;
                let parentGroupTitle;
                let parentData = null;
                // If this report is for a team, show which group it's under,
                // and inherit the due date when the team has none of its own.
                if (data && data.parent) {
                    const parentSnap = await getDoc(doc(db, 'groups', data.parent));
                    parentData = parentSnap.exists() ? parentSnap.data() : null;
                    if (!due) due = parentData && parentData.due;
                    parentGroupTitle = parentData && parentData.name;
                }
                // Prefer the top-level parent's clientid over the team's own —
                // a team's clientid is redundant (not editable in either team
                // dialog) and has been found stale/orphaned on real data.
                const clientId = (parentData && parentData.clientid) || (data && data.clientid);
                let clientData = null;
                if (clientId) {
                    const clientSnap = await getDoc(doc(db, 'clients', clientId));
                    clientData = clientSnap.exists() ? clientSnap.data() : null;
                }
                if (this.group_report && this.group_report.group === groupId) {
                    this._groupDue = due || null;
                    this._parentGroupTitle = parentGroupTitle || null;
                    this._aiFeatureEnabled = cascadeAiEnabled(clientData, data, parentData);
                    this._pdfMode = resolvePdfMode(clientData);
                }
            } catch (e) {
                console.warn('Could not load group due date:', e);
                if (this.group_report && this.group_report.group === groupId) {
                    this._groupDue = null;
                    this._parentGroupTitle = null;
                    this._aiFeatureEnabled = false;
                    this._pdfMode = PDF_MODE_BROWSER;
                }
            }
        }
    }

    // Explains why the "Generate AI Summary" button is disabled, shown on
    // hover (title) and on click (since a disabled state that can't be
    // clicked can't explain itself).
    _aiDisabledMessage() {
        if (this._groupDue === undefined) return translate('ai.checkingAvailability');
        if (!this._groupDue) return translate('ai.noDueDate');
        return translate('ai.availableAfter', { date: formatDate(this._groupDue, { dateStyle: 'medium', timeStyle: 'short' }) });
    }

    get _domainFiles() {
        const { message_domain, relationship_domain, time_domain, space_domain } = this;
        if (!message_domain || !relationship_domain || !time_domain || !space_domain) return null;
        // English regardless of the reader's language: the generated summary is
        // cached per group and shared by everyone in it (see domains.js).
        return { m: englishDomain(message_domain), r: englishDomain(relationship_domain), t: englishDomain(time_domain), s: englishDomain(space_domain) };
    }

    // Only meaningful when this.group_report is a team (i.e.
    // this._parentGroupTitle is set) -- see utility.js's buildTeamMembers.
    get _teamMembers() {
        return buildTeamMembers(this.group_report && this.group_report.users);
    }

    async _getAiSummary() {
        if (this._aiLoading) return;
        this._aiError = undefined;
        this._showAiModal = true;

        if (this._aiSummary) return;

        const domainFiles = this._domainFiles;
        if (!this.report || !this.group_report || !domainFiles) {
            this._aiError = translate('ai.notLoaded');
            return;
        }

        this._aiLoading = true;
        try {
            const combinedUserReport  = buildCombinedReport(this.report, domainFiles);
            const combinedGroupReport = buildCombinedGroupReport(this.group_report, domainFiles);

            // Masquerading only swaps which docs the frontend fetches — it
            // never changes the caller's actual Firebase Auth identity — so
            // this must tell the backend who the report is really for. When
            // not masquerading this equals the caller's own email, which the
            // backend treats as a no-op (see functions/ai.js).
            const fn = httpsCallable(functions, 'generateAiSummary');
            const result = await fn({
                groupId: this.group_report.group,
                targetEmail: this.user_profile && this.user_profile.email,
                combinedUserReport,
                combinedGroupReport,
            });
            this._aiSummary = result.data;
        } catch (e) {
            this._aiError = e.message || translate('ai.generateFailed');
        } finally {
            this._aiLoading = false;
        }
    }

    _reportHeadingAndContext() {
        const contextTitle = this.group_report ? this.group_report.title : '';
        const userName = this.user_profile && (this.user_profile.displayName || this.user_profile.fullName) || '';
        const heading = userName ? translate('ai.headingFor', { name: userName }) : translate('ai.heading');
        return { heading, contextTitle };
    }

    // Builds a standalone HTML document containing only the report, so printing
    // doesn't pull in the rest of the page (shadow DOM can't hide sibling
    // components from a plain window.print() call on the live document).
    _buildPrintableHtml(s) {
        const { heading, contextTitle } = this._reportHeadingAndContext();

        return `<!doctype html>
<html lang="${getLocale()}">
<head>
<meta charset="utf-8">
<title>${heading}</title>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Muli&family=Cormorant+Garamond">
<style>${AI_SUMMARY_PRINT_STYLE}</style>
</head>
<body>
${buildAiSummarySectionHtml(s, {heading, contextTitle, parentGroupTitle: this._parentGroupTitle, members: this._teamMembers})}
</body>
</html>`;
    }

    async _printSummary() {
        const s = this._aiSummary;
        if (!s) return;

        if (this._pdfMode === PDF_MODE_BACKEND) {
            const { heading, contextTitle } = this._reportHeadingAndContext();
            this._pdfLoading = true;
            this._aiError = undefined;
            try {
                await generateAndOpenSummaryPdf({
                    sections: [{ summary: s, heading, contextTitle, parentGroupTitle: this._parentGroupTitle, members: this._teamMembers }],
                });
            } catch (e) {
                this._aiError = translate('ai.pdfFailed', { message: e.message || e });
            } finally {
                this._pdfLoading = false;
            }
            return;
        }

        openPrintWindow(this._buildPrintableHtml(s), {
            onBlocked: () => { this._aiError = translate('ai.popupBlocked'); },
        });
    }

    _closeAiModal() {
        this._showAiModal = false;
        // Send focus back to the control that opened the dialog (WCAG 2.4.3).
        const opener = this.shadowRoot && this.shadowRoot.querySelector('.ai-btn');
        if (opener) opener.focus();
    }

    _renderAiModal() {
        if (!this._showAiModal) return html``;
        const s = this._aiSummary;
        return html`
            <div class="ai-overlay"
                 @click=${(e) => { if (e.target === e.currentTarget) this._closeAiModal(); }}
                 @keydown=${(e) => { if (e.key === 'Escape') { e.stopPropagation(); this._closeAiModal(); } }}>
                <div class="ai-modal" role="dialog" aria-modal="true" aria-labelledby="ai-modal-heading" tabindex="-1">
                    <button class="ai-close" aria-label=${translate('ai.close')} @click=${() => this._closeAiModal()}><span aria-hidden="true">✕</span></button>
                    ${s ? html`
                        <button class="ai-print-btn" ?disabled=${this._pdfLoading} @click=${() => this._printSummary()}>
                            ${this._pdfLoading ? translate('ai.generatingPdf') : (this._pdfMode === PDF_MODE_BACKEND ? translate('ai.downloadPdf') : translate('ai.print'))}
                        </button>
                    ` : html``}
                    <h2 id="ai-modal-heading">${this._reportHeadingAndContext().heading}</h2>
                    ${this.group_report ? html`
                        ${this._parentGroupTitle ? html`
                            <div style="color:#666; margin-bottom:12px;">
                                <div>${tHtml('ai.teamLevel', { title: this.group_report.title })}</div>
                                <div class="team-members">
                                    ${this._teamMembers.map(mem => html`
                                        <span class="team-member">
                                            <intrivity-badge-plain .m=${mem.m} .r=${mem.r} .s=${mem.s} .t=${mem.t} .size=${0.5}></intrivity-badge-plain>
                                            <span>${mem.name}</span>
                                        </span>
                                    `)}
                                </div>
                            </div>
                        ` : html`
                            <div style="color:#666; margin-bottom:12px;">${tHtml('ai.groupLevel', { title: this.group_report.title })}</div>
                        `}
                    ` : html``}

                    ${this._aiLoading ? html`<p>${translate('ai.generating')}</p>` : html``}
                    ${this._aiError   ? html`<p style="color:red;">${this._aiError}</p>`            : html``}

                    ${s ? html`
                        <div class="table-scroll">
                        <table class="summary-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    ${s.domains.map(d => html`
                                        <th style="background:${aiDomainColor(d.code)}">
                                            ${d.name}
                                            <div class="score-line">${translate('ai.scoreLine', { you: d.user_score, team: d.group_score })}</div>
                                        </th>
                                    `)}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>${translate('ai.strengths')}</td>
                                    ${s.domains.map(d => html`
                                        <td>
                                            ${d.strengths.map(item => html`
                                                <div class="item-title">${item.title}</div>
                                                <div class="item-desc">${item.description}</div>
                                            `)}
                                        </td>
                                    `)}
                                </tr>
                                <tr>
                                    <td>${translate('ai.opportunities')}</td>
                                    ${s.domains.map(d => html`
                                        <td>
                                            ${d.opportunities.map(item => html`
                                                <div class="item-title">${item.title}</div>
                                                <div class="item-desc">${item.description}</div>
                                            `)}
                                        </td>
                                    `)}
                                </tr>
                            </tbody>
                        </table>
                        </div>

                        ${s.team_dynamics ? html`
                            <div class="team-dynamics">
                                <strong>${translate('ai.teamDynamics')}</strong> ${s.team_dynamics}
                            </div>
                        ` : html``}

                        <div class="sources">
                            ${translate('ai.sources')}
                            ${AI_SUMMARY_SOURCES.map(src => html`
                                <div>${src.citation}${src.url ? html` <a href=${src.url} target="_blank" rel="noopener">${src.url}</a>` : html``}</div>
                            `)}
                        </div>

                        <div class="disclaimer">${translate('ai.disclaimer')}</div>

                        ${s.generated_at ? html`
                            <div class="generated-at">
                                ${translate('ai.generatedAt', { date: formatAiGeneratedAt(s.generated_at) })}
                            </div>
                        ` : html``}
                    ` : html``}
                </div>
            </div>
        `;
    }

    render() {
        const canShowAi = !this.printable && this.group_report && this.report && this._domainFiles && this._aiFeatureEnabled;
        const duePassed = !!this._groupDue && new Date() >= new Date(this._groupDue);
        return html`
            <div class="banner">
                <div>
                    <h1 style="font-size: 2.1vmax; margin-left: 20px;">
                        ${user ? html`
                            ${this.printable ? html`
                                ${translate('banner.assessmentFor', { name: this.user_profile.displayName })}
                            `: html`
                                ${translate('banner.welcomeBack', { name: this.user_profile.firstName ? this.user_profile.firstName: this.user_profile.displayName })}
                            `}
                        `: html`
                            ${translate('banner.welcome')}
                        `}
                    </h1>
                    <div style="display: flex; align-items: center;">
                        <intrivity-group-selector .group_report=${this.group_report} .group_reports=${this.group_reports}></intrivity-group-selector>
                        ${canShowAi ? html`
                            <button
                                class="ai-btn"
                                ?disabled=${this._aiLoading}
                                title=${duePassed ? '' : this._aiDisabledMessage()}
                                style=${duePassed ? '' : 'opacity: 0.6; cursor: not-allowed;'}
                                @click=${() => {
                                    if (duePassed) {
                                        this._getAiSummary();
                                    } else {
                                        alert(this._aiDisabledMessage());
                                    }
                                }}
                            >
                                <img class="ai-btn-icon" src="./images/ai-icon.svg" alt="" aria-hidden="true" />${this._aiLoading ? translate('common.loading') : translate('ai.generate')}
                            </button>
                        ` : html``}
                    </div>
                </div>

                ${ this.user_profile.paid && this.m && this.r && this.t && this.s ? html`
                        <button type="button" class="unstyled-button banner_badge" @click=${(e) => {
                                    emitEvent(INTRIVITY_NAV_SELECT, { "selection": "profile" }, this);
                                } }>
                            <span class="sr-only">${translate('banner.goToProfile', { m: this.m, r: this.r, t: this.t, s: this.s })}</span>
                            <intrivity-badge-plain aria-hidden="true" .m=${this.m} .r=${this.r} .s=${this.s} .t=${this.t} .size=${this.size}></intrivity-badge-plain>
                            <div aria-hidden="true" style="margin-left: 5px; font-size: larger;">
                                M R T S<br />
                                ${this.m} ${this.r} ${this.t} ${this.s}
                            </div>
                        </button>
                `: html``}
            </div>
            ${this._renderAiModal()}
        `
    }

}
if (!customElements.get('intrivity-name-banner')) customElements.define('intrivity-name-banner', IntrivityNameBannerApp);

export class IntrivityGroupSelector extends Localized(LitElement) {

    static get properties() {
        return {
            group_report: { type: Object },
            group_reports: { type: Array },
            _groupDocs: { type: Object },
        };
    }

    constructor() {
        super();
        this._groupDocs = {};
    }

    // Reads each report's own group/team Firestore doc live -- the cached
    // report JSON has no type/restrict_team_comparison field -- so a
    // top-level group's restrict_team_comparison setting can hide its own
    // aggregate report from the dropdown. This is evaluated per report/group
    // (see _visibleReports), since a user can belong to several groups each
    // with their own setting; the dropdown still aggregates across all of
    // them, just filtering out the ones that are restricted.
    async updated(changedProps) {
        if (!changedProps.has('group_reports') || !this.group_reports) return;
        const docs = {};
        await Promise.all(this.group_reports.map(async (report) => {
            if (!report.group || report.group in docs) return;
            try {
                const snap = await getDoc(doc(db, 'groups', report.group));
                docs[report.group] = snap.exists() ? snap.data() : null;
            } catch (e) {
                console.warn('Could not load group data for comparison filtering:', e);
                docs[report.group] = null;
            }
        }));
        this._groupDocs = docs;
    }

    // Hides a top-level group's own aggregate report from the "Compare with"
    // list when that group has restrict_team_comparison on -- members then
    // only compare against the team(s) they belong to under that group.
    // Team reports are never hidden by this flag. Fails open (keeps a report
    // visible) while its group doc is still loading or couldn't be read,
    // matching the flag's own default-off behavior.
    get _visibleReports() {
        if (!this.group_reports) return [];
        return this.group_reports.filter(report => {
            const data = this._groupDocs[report.group];
            if (!data || data.type !== 'top') return true;
            return !isFlagOn(data.restrict_team_comparison);
        });
    }

    render() {
        return html`
            ${this.group_reports ? html`
                <div style="margin-left: 20px;">
                    <label>
                    ${translate('banner.compareWith')}
                    <select @change=${(e)=>{ emitEvent(INTRIVITY_GROUP_SELECT, JSON.parse(e.target.value), this); }}>
                        ${this._visibleReports.map((report, i) => html`
                            ${report.title ? html`<option ?selected=${report.title === this.group_report.description} value=${JSON.stringify(report)}>${report.title}</option>`: html``}
                        `)}
                    </select>
                    </label>
                </div>
            `: html``}
        `
    }
}
if (!customElements.get('intrivity-group-selector')) customElements.define('intrivity-group-selector', IntrivityGroupSelector);
